import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useBoundaryService } from "../../../hooks";
import ApiTable from "../../../@ui/ApiTable/ApiTable";
import { BoundarySummary } from "../../../types/Boundary/BoundarySummary";
import { ApiTableColumn } from "../../../types/ApiTable/ApiTableColumn";
import { ApiTableState } from "../../../types/ApiTable/ApiTableState";
import { Pagination } from "../../../types/PaginiationResult";
import { FilterType } from "../../../types/ApiTable/ColumnFilter";
import { getBoundaryDisplayName } from "../../../utils/boundaryLabel";

const BoundaryList = () => {
  const { product } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  let page = parseInt(searchParams.get("page") ?? "1");
  if (Number.isNaN(page)) page = 1;
  let pageSize = parseInt(searchParams.get("pageSize") ?? "10");
  if (Number.isNaN(pageSize)) page = 10;
  let nameFilter = searchParams.get("filter");

  const boundaryService = useBoundaryService();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<ApiTableState>({
    pagination: {
      page: page,
      pageSize: pageSize,
    },
    filters: nameFilter
      ? [
          {
            field: "name",
            value: nameFilter,
            filterText: `Name = ${nameFilter}`,
          },
        ]
      : [],
  });
  const [boundaries, setBoundaries] = useState<BoundarySummary[]>([]);
  const [boundariesView, setBoundariesView] = useState<
    Pagination<BoundarySummary>
  >({
    page: 1,
    totalPages: 1,
    records: [],
  });
  const [productName, setProductName] = useState<string | undefined>();

  const cols: ApiTableColumn[] = [
    {
      field: "name",
      label: "Name",
      sortable: false,
      renderContents: (value) => {
        return (
          <a
            href={
              product
                ? `/datasets/${product}/${encodeURIComponent(value.name)}`
                : `/boundaries/${encodeURIComponent(value.name)}`
            }>
            {getBoundaryDisplayName(value)}
          </a>
        );
      },
      filter: {
        type: FilterType.TEXT,
        filterKey: "name",
      },
    },
  ];

  const onStateChange = (state: ApiTableState) => {
    const nameFilter = state.filters?.find((f) => f.field === "name");
    const searchParams: any = {};
    if (state.pagination.page > 1)
      searchParams["page"] = state.pagination.page.toString();
    if (state.pagination.pageSize !== 10)
      searchParams["pageSize"] = state.pagination.pageSize!.toString();
    if (nameFilter) searchParams["filter"] = nameFilter.value;
    setSearchParams(searchParams);
    updateBoundiesView(state, boundaries);
    setState(state);
  };

  const updateBoundiesView = (
    state: ApiTableState,
    boundaries: BoundarySummary[],
  ) => {
    var page = state.pagination.page;
    var pageSize = state.pagination.pageSize ?? 10;
    var start = (page - 1) * pageSize;
    var end = page * pageSize;

    const nameFilter = state.filters?.find((f) => f.field === "name");
    const filteredBoundaries = nameFilter?.value
      ? boundaries.filter(
          (b) =>
            b.name
              .toLocaleLowerCase()
              .indexOf(nameFilter.value.toLocaleLowerCase()) > -1,
        )
      : boundaries;

    setBoundariesView({
      page: page,
      totalPages: Math.ceil(filteredBoundaries.length / pageSize),
      records: filteredBoundaries.slice(start, end),
    });
  };

  useEffect(() => {
    setLoading(true);
    boundaryService
      .listAll()
      .then((boundaries) => {
        setBoundaries(boundaries);
        updateBoundiesView(state, boundaries);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!product) return;
    setProductName(decodeURIComponent(product));
  }, [product]);

  return (
    <div className="bg-light text-black p-3 d-flex flex-column flex-grow-1">
      {productName && <h1>Boundaries for {productName}</h1>}
      {!productName && <h1>Boundaries</h1>}
      <ApiTable
        state={state}
        data={boundariesView}
        columns={cols}
        pageOptions={[10, 20, 50]}
        onChange={onStateChange}
        loading={loading}
      />
    </div>
  );
};

export default BoundaryList;
